import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import MessageDialog from "components/__reusable/MessageDialog";
import SecondaryActionButton from "components/__reusable/SecondaryActionButton";
import PrimaryActionButton from "components/__reusable/PrimaryActionButton";
import ProfileAvatar from "components/__reusable/ProfileAvatar";
import Checkbox from "components/__reusable/Checkbox";
import NextYearClassDropdown from "./NextYearClassDropdown";
import useAppSelector from "hooks/useAppSelector";
import useAllClasses from "hooks/useAllClasses";
import useAppDispatch from "hooks/useAppDispatch";
import { updateClassForStudent } from "features/student/studentAction";
import type { UpdateClassFormData, Student } from "types/Student";
import { setErrorStatus } from "features/error/errorSlice";
import { setSuccessStatus } from "features/success/successSlice";
import type { Class } from "types/Class";
import { isStudentGraduated } from "utilities/StudentStatus";

interface GradeAdvancementDialogProps {
  onYesClick: () => void;
  onNoClick: () => void;
}

interface StudentClassSelection {
  studentId: number;
  newClassId: number | null;
  isSelected: boolean;
}

const GradeAdvancementDialog: React.FC<GradeAdvancementDialogProps> = ({
  onYesClick,
  onNoClick,
}) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const { students, loading } = useAppSelector((state) => state.student);
  const { classes } = useAllClasses();
  const dispatch = useAppDispatch();

  const [studentSelections, setStudentSelections] = useState<
    StudentClassSelection[]
  >([]);
  const [currentClass, setCurrentClass] = useState<Class | null>(null);

  // Filter students to show only those with matching class_id from URL
  const filteredStudents = React.useMemo(() => {
    let targetStudents = students;

    if (id && !isNaN(parseInt(id))) {
      const classIdFromUrl = parseInt(id);
      targetStudents = students.filter(
        (student) => student.class_id === classIdFromUrl
      );
    }

    return targetStudents.filter((student) => !isStudentGraduated(student));
  }, [students, id]);

  useEffect(() => {
    // Use the class ID from URL params instead of student's current class_id
    if (id && !isNaN(parseInt(id))) {
      const classIdFromUrl = parseInt(id);
      const currentClass = classes.find((c) => c.id === classIdFromUrl);
      if (currentClass) {
        setCurrentClass(currentClass);
      }
    }

    if (filteredStudents.length > 0) {
      // Initialize student selections for filtered students only
      const initialSelections = filteredStudents.map((student) => ({
        studentId: student.id,
        newClassId: null,
        isSelected: false,
      }));
      setStudentSelections(initialSelections);
    } else {
      setStudentSelections([]);
    }
  }, [classes, filteredStudents, id]);

  const handleClassChange = (studentId: number, classId: number) => {
    setStudentSelections((prev) =>
      prev.map((selection) =>
        selection.studentId === studentId
          ? { ...selection, newClassId: classId }
          : selection
      )
    );
  };

  const handleCheckboxChange = (studentId: number, isChecked: boolean) => {
    setStudentSelections((prev) =>
      prev.map((selection) =>
        selection.studentId === studentId
          ? {
            ...selection,
            isSelected: isChecked,
            // Reset class selection if unchecked
            newClassId: isChecked ? selection.newClassId : null,
          }
          : selection
      )
    );
  };

  const getStudentClassSelection = (studentId: number): string => {
    const selection = studentSelections.find((s) => s.studentId === studentId);
    return selection?.newClassId?.toString() || "";
  };

  const isStudentSelected = (studentId: number): boolean => {
    const selection = studentSelections.find((s) => s.studentId === studentId);
    return selection?.isSelected || false;
  };

  const isAllStudentsSelected = (): boolean => {
    if (studentSelections.length === 0) return false;
    return studentSelections.every((selection) => selection.isSelected);
  };

  const handleSelectAll = (isChecked: boolean) => {
    setStudentSelections((prev) =>
      prev.map((selection) => ({
        ...selection,
        isSelected: isChecked,
        // Reset class selection if unchecking all
        newClassId: isChecked ? selection.newClassId : null,
      }))
    );
  };

  const disableUpdate = () => {
    if (!currentClass || filteredStudents.length === 0) return true;

    // Get all selected students
    const selectedStudents = studentSelections.filter(
      (selection) => selection.isSelected
    );

    // If no students are selected, disable the button
    if (selectedStudents.length === 0) return true;

    // If any selected student has not chosen a class, disable the button
    return selectedStudents.some((selection) => selection.newClassId === null);
  };

  const closeDialog = () => {
    onNoClick();
  };

  const updateClass = async () => {
    if (filteredStudents.length === 0) return;

    // Get selected students with new class selections
    const studentsToUpdate = studentSelections.filter(
      (selection) => selection.isSelected && selection.newClassId !== null
    );

    if (studentsToUpdate.length === 0) {
      onYesClick();
      return;
    }

    try {
      // Update each student individually
      for (const selection of studentsToUpdate) {
        const formData: UpdateClassFormData = {
          student_ids: [selection.studentId],
          class_id: selection.newClassId!,
        };
        await dispatch(updateClassForStudent({ formData }));
      }

      dispatch(
        setSuccessStatus({
          title: "_student_form._update_student_complete",
          message: "_student_form._update_student_complete_content",
        })
      );
      onYesClick();
    } catch (error: any) {
      dispatch(
        setErrorStatus(
          error.response?.data?.message || "Failed to update student classes"
        )
      );
    }
  };

  const renderStudentName = (student: Student) => {
    if (student.first_name_kata && student.last_name_kata)
      return `${student.last_name} ${student.first_name} (${student.last_name_kata} ${student.first_name_kata})`;
    else return `${student.last_name} ${student.first_name}`;
  };

  return (
    <MessageDialog>
      <div className="h-[75vh] flex flex-col space-y-3">
        {/* Title with pink background */}
        <div className="bg-[#EC6E7D] p-3 rounded-t-lg">
          <h3 className="text-xl font-bold text-center text-white">
            {t("_student._class_advancement_title")}
          </h3>
        </div>

        <div className="px-3">
          <p className="text-sm text-gray-600">
            {t("_student._set_new_class")}
          </p>
          <p className="text-sm text-gray-600">{t("_student._save_warning")}</p>
        </div>
        <div className="flex-1 overflow-auto relative">
          <div className="w-full h-full absolute">
            <table className="w-full relative">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className="w-[10%] p-2 bg-[#f7f7f7] border border-[#D9D9D9] text-center">
                    <Checkbox
                      name="select-all-students"
                      label=""
                      isChecked={isAllStudentsSelected()}
                      onChange={handleSelectAll}
                      aria-label={t("_generic._select_all")}
                    />
                  </th>
                  <th className="w-[55%] p-2 bg-[#f7f7f7] border border-[#D9D9D9] text-center">
                    {t("_student._student_name")}
                  </th>
                  <th className="w-[35%] p-2 bg-[#f7f7f7] border border-[#D9D9D9] text-center">
                    {`${(currentClass?.year || 0) + 1} ${t("_student._class")}`}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredStudents.map((student: Student) => (
                  <tr key={student.id}>
                    <td className="w-[10%] p-2 border border-[#D9D9D9] text-center">
                      <Checkbox
                        name={`student-advancement-${student.id}`}
                        label=""
                        isChecked={isStudentSelected(student.id)}
                        onChange={(isSelected) =>
                          handleCheckboxChange(student.id, isSelected)
                        }
                        aria-label={`${renderStudentName(student)}を選択`}
                      />
                    </td>
                    <td className="w-[55%] p-2 border border-[#D9D9D9]">
                      <div className="flex items-center space-x-3">
                        <ProfileAvatar
                          pictureUrl={student.profile_picture_for_admin || ""}
                        />
                        <span className="text-sm">
                          {renderStudentName(student)}
                        </span>
                      </div>
                    </td>
                    <td className="w-[35%] p-2 border border-[#D9D9D9]">
                      <div
                        className={
                          !isStudentSelected(student.id)
                            ? "opacity-50 pointer-events-none"
                            : ""
                        }
                      >
                        <NextYearClassDropdown
                          classes={classes}
                          currentYear={currentClass?.year || 0}
                          currentClassId={getStudentClassSelection(student.id)}
                          onChange={(classId) =>
                            handleClassChange(student.id, classId)
                          }
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex justify-between items-center space-x-5">
          <div className="w-1/2">
            <SecondaryActionButton
              label={t("_generic._cancel")}
              onClick={closeDialog}
            />
          </div>
          <div className="w-1/2">
            <PrimaryActionButton
              label={t("_student._save")}
              loading={loading}
              disabled={disableUpdate()}
              onClick={updateClass}
            />
          </div>
        </div>
      </div>
    </MessageDialog>
  );
};

export default GradeAdvancementDialog;
